/*
 * The season's communication (sections/pawbau-campaign.liquid), read once and used everywhere:
 * every element with data-pb-camp="<field>" shows that field of the communication running now,
 * or its data-pb-default, or hides. Countdowns tick, and when a deal ends mid-visit everything
 * switches to the next message on its own. Mia's chat asks window.PawbauCampaign directly.
 */
(() => {
  if (window.PawbauCampaign) return;
  const source = document.getElementById('pawbau-campaign');
  if (!source) return;

  let data;
  try {
    data = JSON.parse(source.textContent);
  } catch {
    return;
  }

  const DAY = 86400000;

  const esc = (text) =>
    String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  /** The communication running now: the shortest one whose dates include this minute. */
  const active = () => {
    const comms = Array.isArray(data.comms) ? data.comms : [];
    let asked = null;
    try {
      asked = new URLSearchParams(location.search).get('campaign');
    } catch {}
    if (asked === 'off' || data.mode === 'off') return null;
    if (asked && comms[parseInt(asked, 10) - 1]) return comms[parseInt(asked, 10) - 1];
    const now = Date.now() / 1000;
    let found = null;
    for (const c of comms) {
      if (c.start && c.end && now >= c.start && now < c.end && (!found || c.end - c.start < found.end - found.start)) {
        found = c;
      }
    }
    return found;
  };

  // the same date the delivery line shows: today in the shop's zone, plus the days, on a working day
  const arrival = () => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: data.zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(new Date());
      const get = (type) => Number(parts.find((p) => p.type === type)?.value);
      const holidays = new Set(
        String(data.holidays || '')
          .split(',')
          .map((d) => d.trim())
          .filter(Boolean)
      );
      let time = Date.UTC(get('year'), get('month') - 1, get('day')) + Number(data.days || 16) * DAY;
      const working = (t) => {
        const weekday = new Date(t).getUTCDay();
        return weekday !== 0 && weekday !== 6 && !holidays.has(new Date(t).toISOString().slice(0, 10));
      };
      while (!working(time)) time += DAY;
      return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
        new Date(time)
      );
    } catch {
      return '';
    }
  };

  // the week's order-by, like the promo bar's: the coming Sunday in the shop's zone ("Sunday, Nov 8"),
  // "tomorrow" on Saturday, "tonight" on Sunday. Never past the communication's last day: after its last
  // Sunday it says that day ("Monday, Dec 7"), so Christmas never moves past the cut-off.
  const shopDay = (time) => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: data.zone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(
      new Date(time)
    );
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    return Date.UTC(get('year'), get('month') - 1, get('day'));
  };
  const sunday = (c) => {
    try {
      const today = shopDay(Date.now());
      const weekday = new Date(today).getUTCDay();
      const day = today + ((7 - weekday) % 7) * DAY;
      if (c?.end && c.endLabel && day > shopDay(c.end * 1000)) return c.endLabel;
      if (weekday === 6) return 'tomorrow';
      if (weekday === 0) return 'tonight';
      return new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(day));
    } catch {
      return '';
    }
  };

  const left = (end) => {
    const s = Math.max(0, Math.floor(end - Date.now() / 1000));
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    return d ? `${d}d ${h}h ${m}m` : `${h}h ${m}m`;
  };

  const money = (text) => Number(String(text || '').replace(/[^0-9.]/g, '')) || 0;
  const save = () => {
    const diff = money(data.price) - money(data.extraPrice);
    return diff > 0 ? `$${diff.toFixed(2)}` : '';
  };

  /** Editor text to safe HTML: **bold** and the placeholders. */
  const text = (raw, c = active()) => {
    if (!raw) return '';
    const map = {
      arrival: arrival(),
      sunday: sunday(c),
      extra_price: data.extraPrice || '',
      price: data.price || '',
      save: save(),
      end_date: c?.endLabel || '',
    };
    return esc(raw)
      .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
      .replace(/\r?\n/g, '<br>')
      .replace(/\[(arrival|sunday|extra_price|price|save|end_date)\]/g, (m, k) => esc(map[k]))
      .replace(/\[ends\]/g, () => (c?.end ? `<span data-pb-clock="${c.end}">${left(c.end)}</span>` : ''));
  };

  /** One element: the field of the communication running now, or its default, or nothing. */
  const fill = (el, c = active()) => {
    const field = el.dataset.pbCamp;
    const raw = (c && c[field]) || el.dataset.pbDefault || '';
    const html = text(raw, c);
    if (el.innerHTML !== html) el.innerHTML = html;
    el.hidden = !html;
    const lookEl = el.closest('[data-pb-look]');
    const look = c && c[field] ? c.look || 'light' : 'light';
    if (lookEl && lookEl.dataset.pbLook !== look) lookEl.dataset.pbLook = look;
  };

  const fillAll = () => {
    const c = active();
    document.querySelectorAll('[data-pb-camp]').forEach((el) => fill(el, c));
  };

  let current = active()?.id || null;
  const tick = () => {
    const now = active()?.id || null;
    if (now !== current) {
      current = now;
      fillAll();
      document.dispatchEvent(new CustomEvent('pawbau:campaign'));
      return;
    }
    document.querySelectorAll('[data-pb-clock]').forEach((el) => {
      const t = left(Number(el.dataset.pbClock));
      if (el.textContent !== t) el.textContent = t;
    });
  };

  window.PawbauCampaign = { active, text, fill, fillAll, arrival };

  let queued = false;
  const soon = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      document.querySelectorAll('[data-pb-camp]:not([data-pb-filled])').forEach((el) => {
        el.dataset.pbFilled = '';
        fill(el);
      });
    });
  };

  const start = () => {
    document.querySelectorAll('[data-pb-camp]').forEach((el) => (el.dataset.pbFilled = ''));
    fillAll();
    setInterval(tick, 15000);
    // the cart is redrawn after every change: new offer lines are filled as they appear
    new MutationObserver(soon).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
